import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Loader2, Save, Upload, Image as ImageIcon, ShieldCheck, Send } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULTS = {
  logo_url: "", primary_color: "#193966", accent_color: "#80BC00",
  eyebrow: "QA Asesores · Corredores de Seguros", title: "Solicitá tu cotización",
  subtitle: "Dejanos tus datos y un asesor te contactará a la brevedad.",
  success_title: "¡Solicitud recibida!",
  success_message: "Gracias por contactarnos. Uno de nuestros asesores se comunicará contigo muy pronto.",
  footer_note: "Tus datos se usan únicamente para gestionar tu solicitud de cotización.",
};

export default function LeadFormBrandingDialog({ pipelineId, getAuthHeaders, token, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [b, setB] = useState(DEFAULTS);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}/lead-form-branding`, getAuthHeaders());
      const d = await r.json();
      setB({ ...DEFAULTS, ...(d.branding || {}) });
    } catch { toast.error("Error al cargar la apariencia"); }
    finally { setLoading(false); }
  }, [pipelineId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const upd = (k, v) => setB((s) => ({ ...s, [k]: v }));

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API_URL}/api/articulos/upload-image`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!r.ok) throw new Error();
      const d = await r.json();
      upd("logo_url", d.url);
      toast.success("Logo cargado");
    } catch { toast.error("Error al subir el logo"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}/lead-form-branding`, {
        method: "PUT", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" }, body: JSON.stringify(b) });
      if (!r.ok) throw new Error();
      toast.success("Apariencia guardada"); onClose();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="leadform-branding-dialog">
        <DialogHeader><DialogTitle>Personalizar apariencia del formulario</DialogTitle></DialogHeader>
        {loading ? (
          <div className="text-center p-10"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></div>
        ) : (
          <div className="grid grid-cols-2 gap-5">
            {/* Controls */}
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Logo</Label>
                <div className="flex items-center gap-2 mt-1">
                  {b.logo_url ? <img src={b.logo_url} alt="logo" className="h-10 object-contain border rounded p-1 bg-white" /> : <div className="h-10 w-16 border rounded flex items-center justify-center text-slate-300"><ImageIcon className="w-5 h-5" /></div>}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
                  <Button size="sm" variant="outline" className="h-9" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="branding-upload-logo">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Subir logo
                  </Button>
                  {b.logo_url && <Button size="sm" variant="ghost" className="h-9 text-rose-500" onClick={() => upd("logo_url", "")}>Quitar</Button>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Color principal</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={b.primary_color} onChange={(e) => upd("primary_color", e.target.value)} className="h-9 w-10 rounded border" data-testid="branding-primary" />
                    <Input value={b.primary_color} onChange={(e) => upd("primary_color", e.target.value)} className="h-9 text-xs" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Color de acento</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={b.accent_color} onChange={(e) => upd("accent_color", e.target.value)} className="h-9 w-10 rounded border" data-testid="branding-accent" />
                    <Input value={b.accent_color} onChange={(e) => upd("accent_color", e.target.value)} className="h-9 text-xs" />
                  </div>
                </div>
              </div>
              <div><Label className="text-xs">Texto superior (eyebrow)</Label><Input value={b.eyebrow} onChange={(e) => upd("eyebrow", e.target.value)} className="h-9" data-testid="branding-eyebrow" /></div>
              <div><Label className="text-xs">Título</Label><Input value={b.title} onChange={(e) => upd("title", e.target.value)} className="h-9" data-testid="branding-title" /></div>
              <div><Label className="text-xs">Subtítulo</Label><Textarea value={b.subtitle} onChange={(e) => upd("subtitle", e.target.value)} rows={2} className="text-sm" /></div>
              <div><Label className="text-xs">Título de éxito (al enviar)</Label><Input value={b.success_title} onChange={(e) => upd("success_title", e.target.value)} className="h-9" /></div>
              <div><Label className="text-xs">Mensaje de éxito</Label><Textarea value={b.success_message} onChange={(e) => upd("success_message", e.target.value)} rows={2} className="text-sm" /></div>
              <div><Label className="text-xs">Nota al pie</Label><Input value={b.footer_note} onChange={(e) => upd("footer_note", e.target.value)} className="h-9" /></div>
            </div>

            {/* Live preview */}
            <div>
              <Label className="text-xs text-slate-500">Vista previa</Label>
              <div className="border rounded-xl overflow-hidden shadow-sm mt-1" data-testid="branding-preview">
                <div className="px-5 py-5" style={{ backgroundColor: b.primary_color }}>
                  {b.logo_url && <img src={b.logo_url} alt="logo" className="h-9 object-contain mb-2" />}
                  <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide mb-1" style={{ color: b.accent_color }}>
                    <ShieldCheck className="w-3 h-3" /> {b.eyebrow}
                  </div>
                  <h3 className="text-white text-lg font-bold leading-tight">{b.title}</h3>
                  <p className="text-white/80 text-[11px] mt-0.5">{b.subtitle}</p>
                </div>
                <div className="p-4 space-y-2 bg-white">
                  <div className="h-8 rounded border border-slate-200 bg-slate-50" />
                  <div className="h-8 rounded border border-slate-200 bg-slate-50" />
                  <div className="h-9 rounded text-white text-xs font-semibold flex items-center justify-center gap-1" style={{ backgroundColor: b.accent_color }}>
                    <Send className="w-3 h-3" /> Enviar solicitud
                  </div>
                  <p className="text-[9px] text-slate-400 text-center">{b.footer_note}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving || loading} className="bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="branding-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Guardar apariencia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
